<template>
  <div class="feedback-container">
    <!-- 头部工具栏 -->
    <div class="feedback-header">
      <h2 class="page-title">意见反馈</h2>
      <el-button type="primary" @click="openSubmitDialog" icon="el-icon-edit-outline">
        提交新反馈
      </el-button>
    </div>
    
    <!-- 状态筛选 -->
    <div class="filter-tabs">
      <el-tabs v-model="activeStatus" @tab-click="handleTabClick">
        <el-tab-pane label="全部" name="all"></el-tab-pane>
        <el-tab-pane label="待处理" name="PENDING"></el-tab-pane>
        <el-tab-pane label="处理中" name="PROCESSING"></el-tab-pane>
        <el-tab-pane label="已回复" name="RESOLVED"></el-tab-pane>
        <el-tab-pane label="已关闭" name="CLOSED"></el-tab-pane>
      </el-tabs>
    </div>

    <!-- 反馈列表 -->
    <div class="feedback-list" v-loading="loading" v-if="sessionList  !=null && sessionList.length!==0 ">
      <el-card 
        v-for="session in sessionList" 
        :key="session.id" 
        class="feedback-item"
        :class="{ 'has-unread': session.has_unread }"
      >
        <div class="feedback-item-header">
          <div class="feedback-info">
            <!-- 未读红点 -->
            <span v-if="!session.is_read" class="unread-dot"></span>
            <span class="feedback-title">
              {{ session.title }}
            </span>
            <el-tag :type="getStatusType(session.status)" size="small" class="status-tag">
              {{ getStatusLabel(session.status) }}
            </el-tag>
            <el-tag v-if="session.reply_count > 0" type="info" size="small" class="reply-tag">
              {{ session.reply_count }}条回复
              <span v-if="session.unreadCount > 0" class="unread-count">({{ session.unreadCount }}新)</span>
            </el-tag>
          </div>
          <span class="feedback-time">{{ formatDateTime(session.createdTime) }}</span>
        </div>

        
        <div class="feedback-item-content" @click="viewSessionDetail(session)">
          <p class="feedback-content">{{ getContentPreview(session.content) }}</p>
          <div class="feedback-meta">
            <span v-if="session.created_time" class="last-reply">
              最初提交时间：{{ formatDateTime(session.created_time) }}
            </span>            
            <span v-if="session.last_reply_time" class="last-reply">
              最后回复时间：{{ formatDateTime(session.last_reply_time) }}
            </span>
          </div>
        </div>
        
        <div class="feedback-item-footer">
          <div class="footer-left">
            <span class="feedback-id">会话ID: {{ session.conversation_id }}</span>
          </div>
          <div class="footer-right">
            <el-button type="text" @click="viewSessionDetail(session)">
              查看详情({{ session.reply_count }}个平台回复)
            </el-button>
            <el-button 
              v-if="session.status !== 'CLOSED'" 
              type="text" 
              class="reply-btn"
              @click="openReplyDialog(session)"
            >
              回复
            </el-button>
          </div>
        </div>
      </el-card>
    </div>
    <div v-else>
       <img alt="暂无数据" src="@/assets/images/empty.png" class="empty-img" />
        <a class="empty-text">这里空空如也</a>
     </div>
    <!-- 分页 -->
    <div class="pagination-container" v-if="total > 0">
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>
    
    <!-- 提交反馈弹窗 -->
    <el-dialog
      title="提交意见反馈"
      :visible.sync="submitDialogVisible"
      width="520px"
      @closed="handleSubmitDialogClose"
      custom-class="feedback-submit-dialog"
    >
      <el-form 
        ref="submitFormRef"
        :model="submitForm"
        :rules="submitRules"
        label-width="80px"
      >
        <el-form-item label="标题" prop="title">
          <el-input 
            v-model="submitForm.title" 
            placeholder="请简单描述您的建议或问题"
            maxlength="50"
            show-word-limit
          />
          <div class="form-tip">2-50个字符，简洁描述问题</div>
        </el-form-item>
        
        <el-form-item label="分类" prop="category">
          <el-select v-model="submitForm.category" placeholder="请选择反馈分类" style="width: 100%">
            <el-option
              v-for="item in feedbackCategories"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="优先级" prop="priority">
          <el-radio-group v-model="submitForm.priority">
            <el-radio :label="1">紧急</el-radio>
            <el-radio :label="2">高</el-radio>
            <el-radio :label="3">中</el-radio>
            <el-radio :label="4">低</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item label="内容" prop="content">
          <el-input
            v-model="submitForm.content"
            type="textarea"
            :rows="5"
            placeholder="请详细描述您的问题或建议，我们会尽快处理"
            maxlength="500"
            show-word-limit
          />
          <div class="form-tip">5-500个字符，描述越详细处理越快</div>
        </el-form-item>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="submitDialogVisible = false">取 消</el-button>
        <el-button 
          type="primary" 
          @click="submitFeedback"
          :loading="submitLoading"
        >
          提 交
        </el-button>
      </span>
    </el-dialog>
    
    <!-- 回复反馈弹窗 -->
    <el-dialog
      title="回复反馈"
      :visible.sync="replyDialogVisible"
      width="520px"
      @closed="handleReplyDialogClose"
      custom-class="feedback-reply-dialog"
    >
      <div class="original-feedback" v-if="currentFeedback">
        <div class="original-header">
          <span class="original-title">{{ currentFeedback.title }}</span>
          <el-tag size="mini" type="info">{{ formatTime(currentFeedback.createdTime) }}</el-tag>
        </div>
        <div class="original-content">{{ currentFeedback.content }}</div>
      </div>
      
      <el-form 
        ref="replyFormRef"
        :model="replyForm"
        :rules="replyRules"
        label-width="80px"
        style="margin-top: 20px;"
      >
        <el-form-item label="回复内容" prop="content">
          <el-input
            v-model="replyForm.content"
            type="textarea"
            :rows="4"
            placeholder="请输入您的回复内容"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="replyDialogVisible = false">取 消</el-button>
        <el-button 
          type="primary" 
          @click="submitReply"
          :loading="replyLoading"
        >
          发 送
        </el-button>
      </span>
    </el-dialog>
    
    <!-- 反馈详情弹窗 -->
    <el-dialog title="反馈详情" :visible.sync="detailDialogVisible" width="600px">
      <div v-if="currentDetail" class="feedback-detail">
        <!-- 原始反馈 -->
        <div class="detail-item original">
          <div class="detail-header">
            <span class="sender">{{ currentDetail.originalFeedback.original_feedback?.sender_name || '租户' }}</span>
            <span class="time">{{ formatDateTime(currentDetail.originalFeedback.original_feedback?.created_time) }}</span>
            <el-tag :type="getStatusType(currentDetail.originalFeedback.original_feedback?.status)" size="mini">
              {{ getStatusLabel(currentDetail.originalFeedback.original_feedback?.status) }}
            </el-tag>
          </div>
          <div class="detail-title">{{ currentDetail.originalFeedback.original_feedback?.title }}</div>
          <div class="detail-content">{{ currentDetail.originalFeedback.original_feedback?.content }}</div>
        </div>
        
        <!-- 回复列表 -->
        <div v-if="currentDetail.replies && currentDetail.originalFeedback.replies.length" class="reply-list">
          <div class="reply-title">回复历史</div>
          <div v-for="reply in currentDetail.originalFeedback.replies" :key="reply.id" class="reply-item">
            <div class="reply-header">
              <span class="sender">{{ reply.sender_name === 'admin' ? '管理员' : reply.sender_name }}</span>
              <span class="time">{{ formatDateTime(reply.created_time) }}</span>
              <el-tag size="mini" type="info">回复</el-tag>
            </div>
            <div class="reply-content">{{ reply.content }}</div>
          </div>
        </div>
        
        <!-- 空状态 -->
        <el-empty v-else-if="!currentDetail.replies?.length" description="暂无回复"></el-empty>
      </div>
      
      <div v-else class="loading-tip">
        <el-skeleton :rows="3" animated />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  getFeedbackSessions, 
  getFeedbackDetail, 
  submitFeedback, 
  replyFeedback,
  closeFeedback
} from '@/api/tenant/feedback'
  import { mapState } from 'vuex';
export default {
  name: 'FeedbackList',
  data() {
    return {
      sessionList: [],  // 会话列表
      // 列表数据
      loading: false,
      feedbackList: [],
      currentDetail: null,  // 当前显示的反馈详情
      //detailDialogVisible: false,      
      total: 0,
      currentPage: 1,
      pageSize: 10,
      activeStatus: 'all',
      
      // 提交表单
      submitDialogVisible: false,
      submitLoading: false,
      submitForm: {
        title: '',
        content: '',
        category: 'SUGGESTION',
        priority: 3
      },
      submitRules: {
        title: [
          { required: true, message: '请输入反馈标题', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入反馈内容', trigger: 'blur' },
          { min: 5, max: 500, message: '长度在 5 到 500 个字符', trigger: 'blur' }
        ],
        category: [
          { required: true, message: '请选择反馈分类', trigger: 'change' }
        ],
        priority: [
          { required: true, message: '请选择优先级', trigger: 'change' }
        ]
      },
      
      // 回复相关
      replyDialogVisible: false,
      replyLoading: false,
      replyForm: {
        content: ''
      },
      replyRules: {
        content: [
          { required: true, message: '请输入回复内容', trigger: 'blur' },
          { min: 1, max: 500, message: '内容不能为空', trigger: 'blur' }
        ]
      },
      
      // 详情相关
      detailDialogVisible: false,
      currentFeedback: null,
      replyList: [],
      
      // 分类选项
      feedbackCategories: [
        { value: 'BUG', label: '问题反馈' },
        { value: 'SUGGESTION', label: '功能建议' },
        { value: 'QUESTION', label: '产品咨询' },
        { value: 'COMPLAINT', label: '投诉' },
        { value: 'OTHER', label: '其他' }
      ]
    }
  },
      computed: {
        ...mapState('auth', ['tenantInfo', 'userInfo']),
      },
  mounted() {
    console.log("userInfo", this.userInfo)
    this.loadFeedbackList()
  },
  methods: {
    async loadSessions() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize,
          status: this.activeStatus !== 'all' ? this.activeStatus : null
        }
        
        const response = await getFeedbackSessions(params)  // 新接口
        console.log("getFeedbackSessions",params)
        if (response.data.code === 200 && response.data.data.data) {
          this.sessionList = response.data.data.data || []
          console.log("this.sessionList",this.sessionList)
          this.total = response.data.data.total || 0
        }
      } catch (error) {
        console.error('加载反馈列表失败:', error)
      } finally {
        this.loading = false
      }
    },
  
    // 统一的时间格式化方法
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      
      // 处理数组格式 [year, month, day, hour, minute, second]
      if (Array.isArray(dateTime)) {
        try {
          const year = dateTime[0]
          const month = String(dateTime[1]).padStart(2, '0')
          const day = String(dateTime[2]).padStart(2, '0')
          const hour = String(dateTime[3] || 0).padStart(2, '0')
          const minute = String(dateTime[4] || 0).padStart(2, '0')
          
          return `${year}-${month}-${day} ${hour}:${minute}`
        } catch (e) {
          console.error('日期格式化错误:', e)
          return String(dateTime)
        }
      }
      
      // 处理字符串格式
      if (typeof dateTime === 'string') {
        // 去掉 'T' 和毫秒
        return dateTime.replace('T', ' ').substring(0, 16)
      }
      
      return String(dateTime)
    },    
    // 打开提交弹窗
    openSubmitDialog() {
      this.submitForm = {
        title: '',
        content: '',
        category: 'SUGGESTION',
        priority: 3
      }
      this.submitDialogVisible = true
    },
    
    // 提交反馈
    async submitFeedback() {
      try {
        await this.$refs.submitFormRef.validate()
      } catch (e) {
        return
      }
      
      this.submitLoading = true
      try {
        const response = await submitFeedback(this.submitForm)
        if (response.code === 200) {
          this.$message.success('反馈提交成功')
          this.submitDialogVisible = false
          this.loadFeedbackList()
        } else {
          this.$message.error(response.message || '提交失败')
        }
      } catch (error) {
        console.error('提交反馈失败:', error)
        this.$message.error('提交失败: ' + (error.message || '未知错误'))
      } finally {
        this.submitLoading = false
      }
    },
    
    // 查看反馈详情
    async viewFeedbackDetail(item) {
      console.log("viewFeedbackDetail", item)
      const conversationId = item.conversationId
      
      if (!conversationId) {
        this.$message.error('无法获取会话ID')
        return
      }
      
      this.loading = true
      try {
        const response = await getFeedbackDetail(conversationId)
        console.log('反馈详情响应:', response)  // 调试用
        
        if (response.code === 200 && response.data) {
          // 直接赋值给 currentDetail
          this.currentDetail = response.data
          console.log('currentDetail:', this.currentDetail)  // 调试用
          this.detailDialogVisible = true
        } else {
          this.$message.error(response.message || '获取详情失败')
        }
      } catch (error) {
        console.error('加载反馈详情失败:', error)
        this.$message.error('加载失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    
    // 打开回复弹窗（从列表）
    openReplyDialog(item) {
      // item 是主消息，代表整个会话
      this.currentConversation = {
        id: item.id,
        conversationId: item.conversationId,
        title: item.title,
        status: item.status
      }
      this.replyForm = { content: '' }
      this.replyDialogVisible = true
    },
    
    // 从详情页打开回复
    openReplyFromDetail() {
      this.replyDialogVisible = true
      this.replyForm = { content: '' }
    },
    
    // 提交回复
    async submitReply() {
      if (!this.replyForm.content) {
        this.$message.warning('请输入回复内容')
        return
      }
      
      this.replyLoading = true
      try {
        // 确保传递正确的参数
        const response = await replyFeedback({
          feedbackId: this.currentConversation.id,  // 主消息ID
          content: this.replyForm.content,
          // senderUsername 应该由后端从 token 获取，不需要前端传
        })
        
        if (response.code === 200) {
          this.$message.success('回复成功')
          this.replyDialogVisible = false
          this.loadFeedbackList()
        } else {
          this.$message.error(response.message || '回复失败')
        }
      } catch (error) {
        console.error('回复失败:', error)
        this.$message.error('回复失败: ' + (error.message || '未知错误'))
      } finally {
        this.replyLoading = false
      }
    },
    
    // 判断是否可以回复
    canReply(item) {
      console.log("canReply", item, item.senderType);      
      // 未关闭的++平台管理员发的（回复）反馈可以回复
      return (item.businessType !== 'FEEDBACK_CLOSED')

    },
    
    // 处理标签切换
    // 处理标签点击
    handleTabClick(tab) {
      console.log('切换状态:', tab.name)
      this.currentPage = 1
      this.loadFeedbackList()
    },
    
    // 加载反馈列表
    async loadFeedbackList() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize,
          status: this.activeStatus !== 'all' ? this.activeStatus : null
        }
        
        // 改为调用新接口
        console.log("getFeedbackSessions param:", params)        
        const response = await getFeedbackSessions(params)
        console.log("getFeedbackSessions", response)
        
        if (response.code === 200 && response.data.data) {
          // response.data.content 就是 FeedbackSessionVO 列表
          this.sessionList = response.data.data || []
          this.total = response.data.total || 0
          for(let i=0;i< this.sessionList.length;i++){
            let status =  this.sessionList[i].business_type;
            if(status == null)
               this.sessionList[i].status = 'PENDING'
            else if(status.includes('CLOSED'))
             this.sessionList[i].status = 'CLOSED'
            else if(status.includes('REPLY') || status.includes('PROCESSING'))
             this.sessionList[i].status = 'PROCESSING'
            else
              this.sessionList[i].status = 'PENDING'

          }
        } else {
          this.$message.error(response.message || '加载失败')
        }
      } catch (error) {
        console.error('加载反馈列表失败:', error)
        this.$message.error('加载失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    getStatusLabel(status) {
      
      const map = {
        'PENDING': '待处理',
        'PROCESSING': '处理中',
        'RESOLVED': '已回复',
        'CLOSED': '已关闭'
      }
      return map[status] || '待处理'
    },
 
    
    // 处理分页
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadFeedbackList()
    },
    
    handlePageChange(page) {
      this.currentPage = page
      this.loadFeedbackList()
    },
    
    // 关闭弹窗回调
    handleSubmitDialogClose() {
      this.$refs.submitFormRef?.resetFields()
    },
    
    handleReplyDialogClose() {
      this.$refs.replyFormRef?.resetFields()
    },
    
    handleDetailDialogClose() {
      this.currentFeedback = null
      this.replyList = []
    },
    
    // 工具方法
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    },
    
    getContentPreview(content) {
      if (!content) return ''
      return content.length > 100 ? content.substring(0, 100) + '...' : content
    },
    
    getStatusType(businessType) {
      if (!businessType) return 'info'
      if (businessType.includes('PENDING')) return 'warning'
      if (businessType.includes('PROCESSING')) return 'primary'
      if (businessType.includes('RESOLVED')) return 'success'
      if (businessType.includes('CLOSED')) return 'info'
      return 'info'
    },
    
    getStatusText(businessType) {
      if (!businessType) return '未知'
      if (businessType.includes('PENDING')) return '待处理'
      if (businessType.includes('PROCESSING')) return '处理中'
      if (businessType.includes('RESOLVED')) return '已回复'
      if (businessType.includes('CLOSED')) return '已关闭'
      return '待处理'
    },
    
    getCategoryText(businessType) {
      if (!businessType) return '其他'
      if (businessType.includes('BUG')) return '问题反馈'
      if (businessType.includes('SUGGESTION')) return '功能建议'
      if (businessType.includes('QUESTION')) return '产品咨询'
      if (businessType.includes('COMPLAINT')) return '投诉'
      return '其他'
    },
    
    getPriorityText(priority) {
      const map = { 1: '紧急', 2: '高', 3: '中', 4: '低' }
      return map[priority] || '中'
    },

    // 处理关闭反馈
    handleCloseFeedback(item) {
      this.$confirm('确定要关闭此反馈吗？关闭后将无法继续回复。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await closeFeedback(item.id)
          if (response.code === 200) {
            this.$message.success('反馈已关闭')
            this.loadFeedbackList()
            if (this.detailDialogVisible) {
              this.detailDialogVisible = false
            }
          } else {
            this.$message.error(response.message || '关闭失败')
          }
        } catch (error) {
          console.error('关闭反馈失败:', error)
          this.$message.error('关闭失败: ' + (error.message || '未知错误'))
        }
      }).catch(() => {})
    },

    // 从详情页关闭
    handleCloseFromDetail() {
      this.handleCloseFeedback(this.currentFeedback)
    },
    /**
     * 加载会话详情并打开弹窗
     * @param {string} conversationId - 会话ID
     * @param {object} session - 会话对象（可选，用于更新列表状态）
     */
    async loadConversationDetail(conversationId, session) {
      console.log("loadConversationDetail", conversationId, session)
      if (!conversationId) {
        this.$message.error('会话ID不能为空')
        return
      }
      
      this.detailLoading = true
      this.detailDialogVisible = true
      
      try {
        // 调用获取会话详情的接口
        const response = await getFeedbackDetail(conversationId)
        
        console.log('会话详情响应:', response)
        
        if (response.code === 200 && response.data) {
          // 赋值给 currentDetail
          if (response.data.originalFeedback && response.data.replies) {
            this.currentDetail = response.data
          } else if (Array.isArray(response.data)) {
            this.currentDetail = {
              originalFeedback: response.data[0] || null,
              replies: response.data.slice(1) || []
            }
          } else {
            this.currentDetail = {
              originalFeedback: response.data,
              replies: []
            }
          }
          
          console.log('处理后的详情:', this.currentDetail)
          
          // ===== 新增：收集所有未读消息并标记已读 =====
          const unreadMessages = []
          
          // 检查原始反馈是否未读
          if (this.currentDetail.originalFeedback && !this.currentDetail.originalFeedback.is_tead) {
            unreadMessages.push(this.currentDetail.originalFeedback)
          }
          
          // 检查所有回复是否未读
          if (this.currentDetail.replies && this.currentDetail.replies.length > 0) {
            const unreadReplies = this.currentDetail.replies.filter(r => !r.is_read)
            unreadMessages.push(unreadReplies)

       
          }
       console.info("unreadMessages", unreadMessages)        
          // 标记所有未读消息为已读
          if (unreadMessages.length > 0) {            
            for (const msg of unreadMessages) {
              await this.markFeedbackAsRead(msg.original_feedback.id)   
              for(const reply of msg.replies)           
              await this.markFeedbackAsRead(reply.id)
              // 更新本地对象的 isRead 状态
              msg.is_tead = true
            }
            
            // 刷新顶部菜单的未读计数
            if (window.refreshMessageUnread) {
              window.refreshMessageUnread()
            }
            
            // ===== 关键：更新列表中对应会话的未读状态 =====
            if (session) {
              // 直接更新传入的会话对象
              session.hasUnread = false
            } else if (this.currentDetail.originalFeedback) {
              // 如果没有传入 session，尝试在列表中查找
              const targetSession = this.sessionList.find(
                s => s.conversationId === conversationId || s.id === this.currentDetail.originalFeedback.id
              )
              if (targetSession) {
                // 使用 Vue.set 确保响应式更新
                this.$set(targetSession, 'hasUnread', false)
              }
            }
            
            // 可选：刷新列表数据
            this.loadFeedbackList()
          }
          // ===== 新增结束 =====
          
        } else {
          this.$message.error(response.message || '获取详情失败')
          this.detailDialogVisible = false
        }
      } catch (error) {
        console.error('加载会话详情失败:', error)
        this.$message.error('加载失败: ' + (error.message || '未知错误'))
        this.detailDialogVisible = false
      } finally {
        this.detailLoading = false
      }
    },
    /**
     * 标记反馈为已读
     */
    async markFeedbackAsRead(messageId) {
      console.log("markFeedbackAsRead", messageId)
      try {
        // 调用标记已读接口
        const { markAsRead } = await import('@/api/tenant/message')
        const response = await markAsRead(messageId)
        if (response.code === 200) {
          console.log('标记已读成功:', messageId)
        }
      } catch (error) {
        console.error('标记已读失败:', error)
      }
    },
    /**
     * 从会话列表项点击查看详情
     * @param {object} session - 会话对象
     */
      viewSessionDetail(session) {
        console.log("viewSessionDetail", session)
        const conversationId = session.conversation_id || session.id
        // 传入 session 对象，用于更新列表状态
        this.loadConversationDetail(conversationId, session)
      },      
  }
}
</script>

<style lang="scss" scoped>
.feedback-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.feedback-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  .page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 500;
    color: #303133;
  }
}

.filter-tabs {
  margin-bottom: 20px;
}

.feedback-list {
  min-height: 400px;
}

.feedback-item {
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &.has-reply {
    border-left: 4px solid #67C23A;
  }
  
  .feedback-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    
    .feedback-info {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      
      .feedback-title {
        font-size: 16px;
        font-weight: 500;
        color: #303133;
      }
      
      .status-tag, .reply-tag {
        margin-left: 0;
      }
    }
    
    .feedback-time {
      font-size: 13px;
      color: #909399;
    }
  }
  
  .feedback-item-content {
    margin-bottom: 12px;
    
    .feedback-content {
      margin: 0 0 8px 0;
      color: #606266;
      line-height: 1.6;
    }
    
    .feedback-meta {
      display: flex;
      gap: 20px;
      font-size: 13px;
      color: #909399;
      
      i {
        margin-right: 4px;
      }
    }
  }
  
  .feedback-item-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
    
    .feedback-id {
      font-size: 12px;
      color: #909399;
    }
    
    .footer-right {
      display: flex;
      gap: 16px;
      
      .reply-btn {
        color: #67C23A;
        
        &:hover {
          color: #85ce61;
        }
      }
    }
  }
}

.pagination-container {
  margin-top: 30px;
  text-align: right;
}

// 弹窗样式
:deep(.feedback-submit-dialog),
:deep(.feedback-reply-dialog),
:deep(.feedback-detail-dialog) {
  .el-dialog__body {
    padding: 20px 30px;
    max-height: 70vh;
    overflow-y: auto;
  }
  
  .form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
    line-height: 1.5;
  }
  
  .original-feedback {
    background-color: #f5f7fa;
    padding: 16px;
    border-radius: 6px;
    margin-bottom: 20px;
    
    .original-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      
      .original-title {
        font-weight: 500;
        color: #303133;
      }
    }
    
    .original-content {
      color: #606266;
      line-height: 1.6;
      white-space: pre-wrap;
    }
  }
}

.detail-container {
  .detail-item {
    margin-bottom: 20px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 6px;
    
    &.original {
      background-color: #f0f7ff;
    }
    
    &.reply {
      margin-left: 20px;
      background-color: #f5f5f5;
    }
    
    .detail-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 10px;
      font-size: 14px;
      
      .detail-sender {
        font-weight: 500;
        color: #303133;
      }
      
      .detail-time {
        color: #909399;
        font-size: 13px;
      }
    }
    
    .detail-title {
      font-weight: 500;
      margin-bottom: 8px;
      color: #303133;
    }
    
    .detail-content {
      color: #606266;
      line-height: 1.6;
      margin-bottom: 8px;
      white-space: pre-wrap;
    }
    
    .detail-meta {
      font-size: 13px;
      color: #909399;
      display: flex;
      gap: 20px;
    }
  }
  
  .reply-list {
    margin-top: 20px;
    
    .reply-title {
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 15px;
      color: #303133;
      padding-left: 20px;
    }
  }
}
.feedback-detail {
  padding: 10px;
  
  .detail-item {
    margin-bottom: 20px;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 4px;
    
    &.original {
      background-color: #e3f2fd;
    }
    
    .detail-header {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
      font-size: 14px;
      
      .sender {
        font-weight: bold;
        margin-right: 10px;
      }
      
      .time {
        color: #999;
        margin-right: 10px;
        font-size: 12px;
      }
    }
    
    .detail-title {
      font-weight: bold;
      margin-bottom: 10px;
      font-size: 16px;
    }
    
    .detail-content {
      white-space: pre-wrap;
      line-height: 1.6;
      color: #333;
    }
  }
  
  .reply-list {
    margin-top: 20px;
    
    .reply-title {
      font-weight: bold;
      margin-bottom: 15px;
      color: #666;
    }
    
    .reply-item {
      margin-left: 20px;
      padding: 15px;
      background-color: #f5f5f5;
      border-radius: 4px;
      margin-bottom: 10px;
      
      .reply-header {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        font-size: 13px;
        
        .sender {
          font-weight: 500;
          margin-right: 10px;
        }
        
        .time {
          color: #999;
          margin-right: 10px;
          font-size: 12px;
        }
      }
      
      .reply-content {
        white-space: pre-wrap;
        line-height: 1.6;
        color: #666;
      }
    }
  }
}

.loading-tip {
  padding: 20px;
  text-align: center;
}

.unread-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #f56c6c;
  border-radius: 50%;
  margin-right: 8px;
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(245, 108, 108, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0);
  }
}
.unread-title {
  font-weight: bold;
  color: #303133;
}

.has-unread {
  border-left: 3px solid #f56c6c;
}
  .empty-img{
    display: block;
    margin: 0 auto;
    opacity: 0.45;

  }
   .empty-text{
    color: #999;        
    margin: 0 auto;
    display: block;
    font-family:  微软雅黑;
    font-size: small;
    width: 100%;
    text-align: center;
  } 
</style>